/* ========================================================================= *
 * useTradeLinks — ONE read of `/api/trade-desk/links`, shared by every chip.
 *
 * ⚠️ WHY A MODULE STORE AND NOT A FETCH PER ROW. The Command Center paints a
 * phone's worth of rows at a time and every one of them asks "am I under a
 * trade?". A request per row would be hundreds of calls for one answer, so the
 * first subscriber fetches and every other one reads the same object.
 *
 * ⚠️ THREE STATES, NEVER TWO. `loading`, `ready`, `error`. A chip that could
 * not learn the answer renders nothing ON THE ROW (a row is not the place for
 * a refusal), but every surface that COUNTS trades — the worklist section and
 * the governing-body strip — renders the refusal in words, so an unread link
 * index is never shown as "no trades".
 * ========================================================================= */
import { useSyncExternalStore } from 'react'
import { api, getToken } from './api.js'
import {getSessionGeneration, onSessionGenerationChange} from './sessionGeneration.js'
import { normalizeLinks } from './tradeLinks.js'

const TTL_MS = 60 * 1000
let state = { status: 'idle', links: null, error: '', at: 0 }
let inflight = null
let run = 0
const subs = new Set()
const emit = () => { for (const f of subs) f() }

export function refreshTradeLinks({ force = false } = {}) {
  if (!getToken()) return Promise.resolve(state)
  if (inflight) return inflight
  if (!force && state.status === 'ready' && Date.now() - state.at < TTL_MS) return Promise.resolve(state)
  state = { ...state, status: state.links ? 'refreshing' : 'loading' }
  emit()
  const ticket = ++run, generation = getSessionGeneration()
  const current = () => ticket === run && generation === getSessionGeneration()
  const pending = api.tradeDeskLinks()
    .then((d) => { if (!current()) return; state = { status: 'ready', links: normalizeLinks(d), error: '', at: Date.now() } })
    .catch((e) => {
      if (!current()) return
      state = { status: 'error', links: state.links, at: Date.now(),
        error: e?.body?.reason || e?.body?.detail || e?.message
          || 'The trade links could not be read. This is a refusal, not "no trades".' }
    })
    .finally(() => { if (current()) { inflight = null; emit() } })
  inflight = pending
  return pending
}

function subscribe(fn) {
  subs.add(fn)
  if (state.status === 'idle' || (state.status === 'ready' && Date.now() - state.at >= TTL_MS)) {
    /* Deferred so the store never changes inside React's subscribe call. */
    queueMicrotask(() => { refreshTradeLinks() })
  }
  return () => subs.delete(fn)
}
export const tradeLinksSnapshot = () => state

export function useTradeLinks() {
  return useSyncExternalStore(subscribe, tradeLinksSnapshot, tradeLinksSnapshot)
}

/** Test seam only. */
export function _resetTradeLinks() { run++; state = { status: 'idle', links: null, error: '', at: 0 }; inflight = null }
onSessionGenerationChange(() => {
  _resetTradeLinks()
  emit()
  if (subs.size && getToken()) queueMicrotask(() => { refreshTradeLinks() })
})
